import { NavLink, useLocation } from "react-router-dom"
import { CalendarRange, ListOrdered, Map as MapIcon, MapPin, Package, Settings2, Truck, type LucideIcon } from "lucide-react"
import { cn } from "@/lib/utils"

interface Tab {
  to: string
  label: string
  icon: LucideIcon
}

// Separadores pela ordem do D1 do plano. "Vista do motorista" chega na F2.
const MAIN_TABS: Tab[] = [
  { to: "/despacho", label: "Despacho", icon: MapIcon },
  { to: "/planeamento", label: "Planear o dia", icon: CalendarRange },
  { to: "/encomendas", label: "Encomendas", icon: ListOrdered },
  { to: "/produtos", label: "Produtos (carga)", icon: Package },
]

// Definições: só administradores (botão "Administração" à direita, como no RH).
export const ADMIN_TABS: Tab[] = [
  { to: "/definicoes/transportadoras", label: "Transportadoras e frota", icon: Truck },
  { to: "/definicoes/codigos-postais", label: "Códigos postais", icon: MapPin },
]

/**
 * Pílulas no estilo "glass" do host Koortex (igual à app de RH): contentor
 * rounded-full translúcido, pílula ativa preenchida com a cor do tenant.
 */
export function PillNav({ tabs, size = "md", className }: { tabs: Tab[]; size?: "sm" | "md"; className?: string }) {
  return (
    <nav
      className={cn(
        "inline-flex max-w-full items-center gap-1 overflow-x-auto rounded-full border border-border/60 bg-card/70 p-1 shadow-sm backdrop-blur",
        className,
      )}
    >
      {tabs.map((tab) => {
        const Icon = tab.icon
        return (
          <NavLink
            key={tab.to}
            to={tab.to}
            title={tab.label}
            className={({ isActive }) =>
              cn(
                "inline-flex shrink-0 items-center gap-1.5 rounded-full font-medium transition-colors",
                size === "sm" ? "px-3 py-1 text-xs" : "px-3.5 py-1.5 text-sm",
                isActive
                  ? "bg-accent text-accent-foreground shadow-sm"
                  : "text-muted-foreground hover:bg-secondary/70 hover:text-foreground",
              )
            }
          >
            <Icon className={size === "sm" ? "h-3 w-3" : "h-3.5 w-3.5"} />
            <span className={size === "md" ? "hidden sm:inline" : undefined}>{tab.label}</span>
          </NavLink>
        )
      })}
    </nav>
  )
}

export function TabNav({ isAdmin }: { isAdmin: boolean }) {
  const { pathname } = useLocation()
  const inAdmin = pathname.startsWith("/definicoes")
  return (
    <header className="flex flex-wrap items-center justify-between gap-3 px-4 pb-3 pt-4">
      <div className="flex min-w-0 flex-wrap items-center gap-3">
        <h1 className="text-lg font-semibold">Otimizador Logístico</h1>
        <PillNav tabs={MAIN_TABS} />
      </div>
      {isAdmin ? (
        <NavLink
          to={ADMIN_TABS[0].to}
          className={cn(
            "inline-flex items-center gap-1.5 rounded-full border px-3.5 py-1.5 text-sm font-medium transition-colors",
            inAdmin
              ? "border-transparent bg-accent text-accent-foreground shadow-sm"
              : "border-border/60 bg-card/70 text-muted-foreground shadow-sm backdrop-blur hover:bg-secondary/70 hover:text-foreground",
          )}
        >
          <Settings2 className="h-3.5 w-3.5" />
          Administração
        </NavLink>
      ) : null}
    </header>
  )
}
